/* Miela product page: gallery, purchase options, variant pills, quantity, sticky bar. */
(() => {
  const fmt = (cents, format) => {
    const value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
  };

  /* ---------- Gallery ---------- */
  class MielaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-go]')];
      this.dots = [...this.querySelectorAll('.m-gallery__dot')];
      this.index = 0;
      if (this.slides.length < 2) return;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.go))));
      this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => this.go(this.index + Number(b.dataset.step))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });

      // Whichever slide is most in view is the active one (covers swipes too)
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.intersectionRatio > 0.6) this.mark(this.slides.indexOf(en.target)); });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => io.observe(s));
    }

    go(i) {
      const n = this.slides.length;
      const next = ((i % n) + n) % n;
      const slide = this.slides[next];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2, behavior: 'smooth' });
      this.mark(next);
    }

    mark(i) {
      if (i < 0) return;
      this.index = i;
      this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.setAttribute('aria-selected', k === i); });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      const thumb = this.thumbs[i];
      if (thumb && thumb.parentElement.scrollHeight > thumb.parentElement.clientHeight) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }
  if (!customElements.get('miela-gallery')) customElements.define('miela-gallery', MielaGallery);

  /* ---------- Buy box ---------- */
  const init = (root) => {
    const formEl = root.querySelector('miela-product-form');
    if (!formEl || formEl.dataset.ready) return;
    formEl.dataset.ready = '1';
    const data = JSON.parse(formEl.querySelector('[data-variants]').textContent);
    const form = formEl.querySelector('form');
    const variantInput = form.querySelector('[data-variant-id]');
    const planInput = form.querySelector('[data-plan-input]');
    const planSelect = form.querySelector('[data-plan-select]');
    const addBtn = form.querySelector('[data-add]');
    const addLabel = form.querySelector('[data-add-label]');
    const addPrice = form.querySelector('[data-add-price]');
    const express = form.querySelector('[data-express]');
    const qtyInput = form.querySelector('[data-qty-input]');
    const priceEl = root.querySelector('[data-price]');
    const labelText = addLabel?.textContent.trim() || 'Add to cart';

    const variant = () => data.variants.find((v) => String(v.id) === variantInput.value) || data.variants[0];
    const subscribing = () => form.querySelector('[data-purchase]:checked')?.value === 'subscribe';

    const update = () => {
      const v = variant();
      const sub = subscribing();
      const planId = sub && planSelect ? planSelect.value : '';
      planInput.value = planId;
      const unit = planId && v.plans[planId] !== undefined ? v.plans[planId] : v.price;
      const qty = Math.max(1, Number(qtyInput?.value) || 1);

      form.querySelectorAll('[data-plan-card]').forEach((c) => c.classList.toggle('is-selected', c.querySelector('input').checked));
      const planPriceEl = form.querySelector('[data-plan-price]');
      if (planPriceEl && planSelect) planPriceEl.textContent = fmt(v.plans[planSelect.value] ?? v.price, data.money);
      const onceEl = form.querySelector('[data-once-price]');
      if (onceEl) onceEl.textContent = fmt(v.price, data.money);
      if (priceEl) priceEl.textContent = fmt(v.price, data.money);
      if (addPrice) addPrice.textContent = fmt(unit * qty, data.money);
      if (addLabel) addLabel.textContent = v.available ? labelText : 'Sold out';
      addBtn.setAttribute('aria-disabled', v.available ? 'false' : 'true');
      if (express) express.hidden = sub;
      document.querySelectorAll('[data-sticky-price]').forEach((el) => (el.textContent = fmt(unit * qty, data.money)));
    };

    form.addEventListener('change', (e) => {
      if (e.target.name?.startsWith('option-')) {
        const chosen = [...form.querySelectorAll('[data-option]')].map((fs) => fs.querySelector('input:checked')?.value);
        const match = data.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
        if (match) {
          variantInput.value = match.id;
          const url = new URL(location.href); url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url);
        }
      }
      update();
    });
    form.querySelectorAll('[data-step-qty]').forEach((b) => b.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(b.dataset.stepQty));
      update();
    }));
    qtyInput?.addEventListener('input', update);
    update();

    /* Mobile sticky bar: shows once the main button scrolls away */
    const bar = document.querySelector('[data-stickybar]');
    if (bar) {
      bar.hidden = false;
      new IntersectionObserver(([en]) => bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(addBtn);
      bar.querySelector('[data-sticky-add]').addEventListener('click', () => form.requestSubmit());
    }
  };

  const boot = () => document.querySelectorAll('.m-pdp').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('.m-pdp').forEach(init));
})();
